<script setup lang="ts">
import DateTimePickerField from '@/components/Fields/DateTimePickerField.vue'
import { StackLayout } from '@/components/Layouts'
import FullScreenLoader from '@/components/LoadingSpinner/FullScreenLoader.vue'
import { BaseDialog } from '@/components/Modals/Dialog'
import store from '@/store'
import { EAlertActions } from '@/store/alertStore/AlertStoreTypes'
import { EStoreModules } from '@/store/storeType'
import moment from 'moment'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import PrimaryButton from '../../../components/Buttons/PrimaryButton.vue'
import CheckboxField from '../../../components/Fields/CheckboxField.vue'
import NumberInputField from '../../../components/Fields/NumberInputField.vue'
import DeviceSelector from '../../../components/Selector/components/DeviceSelector.vue'
import detectionApi from '../../../services/detection.service'
import { type DetectionCreateModel } from '../../../types/models/DetectionModel'
import type { EventModel } from '../../../types/models/EventModel'

type Props = {
    deviceId?: number
    event?: EventModel
}
const props = defineProps<Props>()
type Emits = {
    (e: 'refresh'): void
}
const emit = defineEmits<Emits>()

const { t } = useI18n()

const open = ref(false)
const isLoading = ref(false)
const submitted = ref(false)

const hasErrors = computed(() => ({
    bib: submitted.value && !data.value.bib,
    timestamp: submitted.value && !data.value.timestamp
}))

const data = ref<Partial<DetectionCreateModel>>({
    device_id: props.deviceId,
    timestamp: new Date()
})

const createDetection = () => {
    submitted.value = true
    if (!props.deviceId || !data.value?.bib || !data.value.timestamp) return
    isLoading.value = true
    detectionApi
        .createDetection({
            ...data.value,
            device_id: props.deviceId ?? data.value?.device_id,
            timestamp: parseInt(moment(data.value.timestamp).format('X'))
        })
        .then(() => {
            open.value = false
            data.value = {
                device_id: props.deviceId,
                timestamp: new Date()
            }

            store.dispatch(`${EStoreModules.ALERT}/${EAlertActions.QUEUE_ITEM}`, {
                action: EAlertActions.SUCCESS,
                message: t('device.actions.addDetection.createDetectionSuccess')
            })

            setTimeout(() => emit('refresh'), 5000)
        })
        .finally(() => {
            isLoading.value = false
            submitted.value = false
        })
}
</script>

<template>
    <FullScreenLoader :isLoading="isLoading" />
    <PrimaryButton
        :label="$t('device.actions.addDetection.action')"
        @click="open = true"
        v-bind="$attrs" />
    <BaseDialog
        :title="$t('device.actions.addDetection.title')"
        :open="open"
        @close="open = false"
        @confirm="createDetection"
        class="dialog-with-dropdown">
        <template #content>
            <StackLayout direction="column" :gap="16" class="full-width">
                <DeviceSelector
                    v-if="!deviceId"
                    :label="$t('detection.model.device')"
                    :selectedDevice="data?.device_id?.toString()"
                    @select="
                        data = { ...(data ?? {}), device_id: $event?.device_id ?? $event?.id }
                    " />
                <NumberInputField
                    :label="$t('detection.model.bib')"
                    :modelValue="data?.bib"
                    :hasError="hasErrors.bib"
                    @change="data = { ...(data ?? {}), bib: $event }" />
                <NumberInputField
                    :label="$t('detection.model.eventFilter')"
                    :modelValue="data?.event_filter"
                    @change="data = { ...(data ?? {}), event_filter: $event }" />
                <NumberInputField
                    :label="$t('detection.model.timerFilter')"
                    :modelValue="data?.timer_filter"
                    @change="data = { ...(data ?? {}), timer_filter: $event }" />
                <CheckboxField
                    :label="$t('detection.model.withdrawal')"
                    :modelValue="!!data?.withdrawal"
                    @click="data = { ...(data ?? {}), withdrawal: $event }" />
                <DateTimePickerField
                    :label="$t('device.actions.addDetection.timestamp')"
                    :modelValue="data?.timestamp ? moment(data?.timestamp).toDate() : undefined"
                    :timepicker="{ enableSeconds: true }"
                    :hasError="hasErrors.timestamp"
                    :minDatetime="event ? moment(event?.start_at).utc().toDate() : undefined"
                    :maxDatetime="event ? moment(event?.end_at).utc().toDate() : undefined"
                    enableSeconds
                    @change="data = { ...(data ?? {}), timestamp: $event }"
                    class="full-width" />
            </StackLayout>
        </template>
    </BaseDialog>
</template>
